import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { areArraysOfObjectsEqual } from '@elab/core/utils/objectUtils';

import { selectors as userSelectors } from '@elab/core/store/user';
import { actions as uiActions } from '@elab/core/store/ui';
import { interestSelectionComponent } from '@elab/core/utils/constants';

import * as api from './api';

import { actions, selectors, types } from '.';

const INTEREST_SELECTION_POPUP = 'interest-selection-at-login-popup';

function* getEndPointDetails(serviceType) {
    const baseUrl = yield select(selectors.getNavigationCategoryBaseUrl);
    const apiKey = yield select(selectors.getNavigationCategoryApiKey);
    const serviceUrl = yield select(selectors.getServiceUrl, serviceType);
    return { baseUrl, apiKey, serviceUrl };
}

export function* getNavigationCategoryTags({ payload }) {
    const serviceType = payload.serviceType || 'categoryNavNode';
    const { baseUrl, apiKey, serviceUrl } = yield call(getEndPointDetails, serviceType);
    payload.baseUrl = baseUrl;
    payload.apiKey = apiKey;
    payload.serviceUrl = serviceUrl;
    const categoryTags = yield select(selectors.getNavigationCategoryTags);
    try {
        if (
            categoryTags &&
            payload.categoryId in categoryTags &&
            categoryTags[payload.categoryId].length
        ) {
            yield call(
                setCategoryTags,
                categoryTags[payload.categoryId],
                payload.linkUrl,
                payload.categoryId,
            );
        } else {
            yield call(setCategoryTags, [], payload.linkUrl, payload.categoryId);
        }
        if (!payload.categoryId) {
            return getNavigationCategoryTags;
        }
        const response = yield call(api.getNavigationCategoryTags, payload);
        const responseData = response.tag_tr
            ? response.tag_tr.sort((a, b) => b.count - a.count)
            : [];
        if (payload.customAction) {
            yield put(actions.getCategoryPageTags(responseData));
        }
        if (categoryTags[payload.categoryId] && categoryTags[payload.categoryId].length) {
            if (!areArraysOfObjectsEqual(responseData, categoryTags[payload.categoryId])) {
                yield call(setCategoryTags, responseData, payload.linkUrl, payload.categoryId);
            }
        } else {
            yield call(setCategoryTags, responseData, payload.linkUrl, payload.categoryId);
        }
    } catch (e) {
        console.error('Exeption occured in fetching the category tags: ', e);
    }
}

function* setCategoryTags(tags, linkUrl, activeTagMenu) {
    yield put(
        actions.setNavigationCategoryTags({
            tags,
            linkUrl,
            activeTagMenu,
        }),
    );
}

function sortAndSliceArray(targetArray, field, doSlice = false, length = 5) {
    targetArray = targetArray.sort((a, b) => b[field] - a[field]);
    if (doSlice) {
        targetArray = targetArray.slice(0, length);
    }
    return targetArray;
}

export function* fetchRelatedKeywords({ payload }) {
    const { keyWord, category } = payload;
    const { baseUrl, apiKey, serviceUrl } = yield call(
        getEndPointDetails,
        'categoryKeywordPlpNavNode',
    );
    try {
        const response = yield call(
            api.fetchRelatedKeyword,
            baseUrl,
            keyWord,
            apiKey,
            category,
            serviceUrl,
        );
        let shouldFetchProductList = true;
        if (response) {
            const { base_products: baseProducts, tag_tr: tag } = response;
            const relatedKeywords =
                response.relation_keywords && response.relation_keywords.length > 0
                    ? sortAndSliceArray(response.relation_keywords, 'count', true, 5)
                    : [];
            yield put(actions.setRelatedKeywords({ relatedKeywords, baseProducts, tag }));
            shouldFetchProductList = baseProducts.length > 0;
        }
        if (shouldFetchProductList) {
            yield put(actions.getProductList({ keyWord, category }));
        } else {
            yield put(actions.setProductsList([]));
        }
    } catch (error) {
        console.error('Exception occurred in fetching the category tags: ', error);
    }
}

export function* fetchProductList({ payload }) {
    const pageSize = yield select(selectors.getCategoryPlpSize);
    let { keyWord, category, page = 1 } = payload;
    page -= 0;
    const pageCodeList = yield select(selectors.getBaseProductList);
    const total = pageCodeList.length;
    yield call(setCurrentPage, total, page, pageSize, keyWord, category);
    const startPoint = page === 1 ? page : (page - 1) * pageSize + 1;
    const endPoint = pageSize * page;
    const codes = pageCodeList.slice(startPoint - 1, endPoint);
    const productResponse = yield call(api.getProductList, codes);
    if (!productResponse.errors) {
        yield put(actions.setProductsList(Object.values(productResponse.products)));
    }
}

export function* setCurrentPage(total, page, size, keyword, category) {
    const pagination = yield call(getPagination, total, page, size, keyword, category);
    yield put(actions.setCurrentPage(pagination));
}

export const getPagination = (total, currentPage = 0, pageSize = 20, keyword, category) => ({
    totalPages: Math.ceil(total / pageSize),
    totalResults: total,
    currentPage,
    pageSize,
    keyword,
    category,
});

export function* getPage({ payload }) {
    const { page, size } = payload;
    yield call(getNextProduct, page, size);
}

export function* getNextProduct(page, pageSize) {
    const keyWord = yield select(selectors.getKeywordForPagination);
    const category = yield select(selectors.getCategoryForPagination);
    yield call(fetchProductList, { payload: { keyWord, category, page, pageSize } });
}

export function* getInterestSelectionPopupContent() {
    const userId = yield select(userSelectors.getUserId);
    const data = yield call(
        api.getInterestSelectionPopupContent,
        userId,
        interestSelectionComponent.parent,
    );

    if (data.interestSelectionQuestionnaireComponents) {
        const components = data.interestSelectionQuestionnaireComponents.split(' ');
        yield put(actions.setInterestSelectionComponents(components));

        if (components && components[0] && components[0].includes('Category')) {
            const response = yield call(
                api.getInterestSelectionPopupContent,
                userId,
                components[0],
            );
            yield put(actions.setInterestSelectionComponentData(response));
            yield put(uiActions.openDialog({ name: INTEREST_SELECTION_POPUP }));
            yield call(api.sendLastInterestSelectionTime);
        }
    }
}

export function* getCategoryKeywords(categories) {
    const payload = yield call(getEndPointDetails, 'categoryNavNode');
    const tagsToShow = {};
    try {
        for (const item of categories) {
            payload.categoryId = item;
            const response = yield call(api.getNavigationCategoryTags, payload);
            const responseData = response.tag_tr
                ? response.tag_tr.sort((a, b) => b.count - a.count)
                : [];
            if (responseData.length) {
                for (let i = 0; i < 3; i++) {
                    tagsToShow[responseData[i].display] = responseData[i].original;
                }
            }
        }
        if (Object.keys(tagsToShow).length) {
            yield put(actions.setInterestSelectionKeywords(tagsToShow));
        } else {
            yield put(uiActions.closeDialogByName(INTEREST_SELECTION_POPUP));
        }
    } catch (error) {
        yield put(uiActions.closeDialogByName(INTEREST_SELECTION_POPUP));
    }
}

export function* interestSelectionStepSubmitSaga({ payload }) {
    if (!payload) return;

    const { currentIndex, tagNames, eventName, categories } = payload;
    const userId = yield select(userSelectors.getUserId);
    const components = yield select(selectors.getInterestSelectionComponents);
    const isLastStep = components.length === currentIndex + 1;
    const currentDate = new Date();
    const isoTimestamp = currentDate.toISOString().split('.')[0] + 'Z';

    if (isLastStep) {
        // Event will be listened by insider for interest selection popup final submit for keywords
        const event = new CustomEvent('interest-selection-keywords', {
            detail: {
                event_name: eventName,
                timestamp: isoTimestamp,
                event_params: {
                    keywords: tagNames.join(', '),
                },
            },
        });
        window.dispatchEvent(event);
        yield put(uiActions.closeDialogByName(INTEREST_SELECTION_POPUP));
    } else {
        // Event will be listened by insider for interest selection popup for categories
        const event = new CustomEvent('interest-selection-categories', {
            detail: {
                event_name: eventName,
                timestamp: isoTimestamp,
                event_params: {
                    category_name: tagNames.join(', '),
                },
            },
        });
        window.dispatchEvent(event);
        if (
            components &&
            components[currentIndex + 1] &&
            components[currentIndex + 1].includes('Keyword')
        ) {
            yield call(getCategoryKeywords, categories);
            const response = yield call(
                api.getInterestSelectionPopupContent,
                userId,
                components[currentIndex + 1],
            );
            yield put(actions.setInterestSelectionComponentData(response));
        } else {
            yield put(uiActions.closeDialogByName(INTEREST_SELECTION_POPUP));
        }
    }
}

export default [
    takeLatest(types.GET_NAVIGATION_CATEGORY_TAGS, getNavigationCategoryTags),
    takeEvery(types.FETCH_RELATED_KEYWORDS, fetchRelatedKeywords),
    takeEvery(types.GET_PRODUCT_LIST, fetchProductList),
    takeEvery(types.GET_INTEREST_SELECTION_POPUP_CONTENT, getInterestSelectionPopupContent),
    takeEvery(types.INTEREST_SELECTION_STEP_SUBMIT, interestSelectionStepSubmitSaga),
    takeLatest(types.GET_NEXT_PAGE, getPage),
];
